import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { firestore } from './client';
import type {
  Order,
  OrderSummary,
  OrderFilters,
  OrderStats,
  OrderItem,
} from 'utils/types/Order';
import type { Beer } from 'utils/types/Beer';
import { mockBeers } from 'utils/data/mockBeers';

const ORDERS_COLLECTION = 'orders';

// Interface para item do pedido com dados completos da cerveja
export interface OrderItemWithBeerData extends OrderItem {
  beerImage?: string;
  beerStyle?: string;
  beerAbv?: number;
  beerVolume?: number;
}

// Interface para ordem com itens enriquecidos
export interface OrderWithBeerData extends Omit<Order, 'items'> {
  items: OrderItemWithBeerData[];
}

/**
 * Busca dados da cerveja por ID nos mockBeers
 */
const getBeerDataById = (beerId: string): Beer | undefined => {
  return mockBeers.find(beer => beer.id === beerId);
};

/**
 * Enriquece itens do pedido com dados completos das cervejas
 */
const enrichOrderItemsWithBeerData = (
  items: OrderItem[],
): OrderItemWithBeerData[] => {
  return items.map(item => {
    const beerData = getBeerDataById(item.beerId);
    return {
      ...item,
      beerImage: beerData?.image,
      beerStyle: beerData?.style,
      beerAbv: beerData?.abv,
      beerVolume: beerData?.volume,
    };
  });
};

/**
 * Gera um número sequencial para o pedido
 */
const generateOrderNumber = async (): Promise<string> => {
  try {
    // console.log('🔢 Gerando número do pedido...');

    const year = new Date().getFullYear();
    const yearStr = year.toString();

    // console.log('📅 Ano atual:', yearStr);

    // Buscar o último pedido do ano
    const ordersRef = collection(firestore, ORDERS_COLLECTION);
    const q = query(
      ordersRef,
      where('orderNumber', '>=', `CLB-${yearStr}-000`),
      where('orderNumber', '<', `CLB-${yearStr}-999`),
      orderBy('orderNumber', 'desc'),
      limit(1),
    );

    // console.log('🔍 Buscando último pedido do ano...');
    const snapshot = await getDocs(q);
    // console.log('📊 Pedidos encontrados:', snapshot.size);

    let nextNumber = 1;
    if (!snapshot.empty) {
      const lastOrder = snapshot.docs[0].data();
      // console.log('📋 Último pedido encontrado:', lastOrder.orderNumber);
      const lastNumber = parseInt(lastOrder.orderNumber.split('-')[2]);
      nextNumber = lastNumber + 1;
    }

    const orderNumber = `CLB-${yearStr}-${nextNumber
      .toString()
      .padStart(3, '0')}`;
    // console.log('✨ Número do pedido gerado:', orderNumber);

    return orderNumber;
  } catch (error) {
    // Fallback resiliente para não bloquear a criação do pedido
    const year = new Date().getFullYear();
    const yearStr = year.toString();
    const suffix = (Date.now() % 1000).toString().padStart(3, '0');
    const fallbackOrderNumber = `CLB-${yearStr}-${suffix}`;
    console.warn(
      '⚠️ Falha ao buscar último pedido. Usando número de pedido alternativo:',
      fallbackOrderNumber,
      error,
    );
    return fallbackOrderNumber;
  }
};

/**
 * Converte uma Order para o formato do Firestore
 */
const orderToFirestore = (order: Omit<Order, 'id'>): any => {
  const firestoreOrder: any = {
    ...order,
    createdAt: Timestamp.fromDate(order.createdAt),
    updatedAt: Timestamp.fromDate(order.updatedAt),
    confirmedAt: order.confirmedAt
      ? Timestamp.fromDate(order.confirmedAt)
      : null,
    shippedAt: order.shippedAt ? Timestamp.fromDate(order.shippedAt) : null,
    deliveredAt: order.deliveredAt
      ? Timestamp.fromDate(order.deliveredAt)
      : null,
    cancelledAt: order.cancelledAt
      ? Timestamp.fromDate(order.cancelledAt)
      : null,
    payment: {
      ...order.payment,
      processedAt: order.payment.processedAt
        ? Timestamp.fromDate(order.payment.processedAt)
        : null,
      approvedAt: order.payment.approvedAt
        ? Timestamp.fromDate(order.payment.approvedAt)
        : null,
      refundedAt: order.payment.refundedAt
        ? Timestamp.fromDate(order.payment.refundedAt)
        : null,
    },
  };

  // Adicionar tracking apenas se existir
  if (order.tracking) {
    firestoreOrder.tracking = {
      ...order.tracking,
      lastUpdate: order.tracking.lastUpdate
        ? Timestamp.fromDate(order.tracking.lastUpdate)
        : null,
    };
  }

  return firestoreOrder;
};

/**
 * Converte dados do Firestore para Order
 */
const firestoreToOrder = (id: string, data: any): Order => {
  return {
    ...data,
    id,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    confirmedAt: data.confirmedAt?.toDate() || undefined,
    shippedAt: data.shippedAt?.toDate() || undefined,
    deliveredAt: data.deliveredAt?.toDate() || undefined,
    cancelledAt: data.cancelledAt?.toDate() || undefined,
    payment: {
      ...data.payment,
      processedAt: data.payment?.processedAt?.toDate() || undefined,
      approvedAt: data.payment?.approvedAt?.toDate() || undefined,
      refundedAt: data.payment?.refundedAt?.toDate() || undefined,
    },
    tracking: data.tracking
      ? {
          ...data.tracking,
          lastUpdate: data.tracking.lastUpdate?.toDate() || undefined,
        }
      : undefined,
  };
};

/**
 * Cria um novo pedido
 */
export const createOrder = async (
  orderData: Omit<Order, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'>,
): Promise<Order> => {
  try {
    // console.log('🛒 Iniciando criação de pedido:', {
    //   userId: orderData.userId,
    //   itemsCount: orderData.items.length,
    //   total: orderData.total,
    // });

    const orderNumber = await generateOrderNumber();
    const now = new Date();

    const order: Omit<Order, 'id'> = {
      ...orderData,
      orderNumber,
      createdAt: now,
      updatedAt: now,
    };

    // console.log('📋 Número do pedido gerado:', orderNumber);

    const firestoreData = orderToFirestore(order);
    // console.log('🔄 Dados para Firestore preparados:', {
    //   orderNumber: firestoreData.orderNumber,
    //   hasPayment: !!firestoreData.payment,
    //   hasShipping: !!firestoreData.shipping,
    //   itemsCount: firestoreData.items?.length,
    // });

    const docRef = await addDoc(
      collection(firestore, ORDERS_COLLECTION),
      firestoreData,
    );

    // console.log('✅ Pedido criado com sucesso no Firestore:', {
    //   id: docRef.id,
    //   orderNumber,
    // });

    return {
      ...order,
      id: docRef.id,
    };
  } catch (error) {
    console.error('❌ Erro ao criar pedido:', error);
    console.error('📊 Dados do pedido que falharam:', {
      userId: orderData.userId,
      itemsCount: orderData.items?.length,
      total: orderData.total,
      hasPayment: !!orderData.payment,
      hasShipping: !!orderData.shipping,
    });
    throw new Error('Falha ao criar pedido');
  }
};

/**
 * Busca um pedido por ID
 */
export const getOrderById = async (orderId: string): Promise<Order | null> => {
  try {
    const docRef = doc(firestore, ORDERS_COLLECTION, orderId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return firestoreToOrder(docSnap.id, docSnap.data());
    }

    return null;
  } catch (error) {
    console.error('Erro ao buscar pedido:', error);
    throw new Error('Falha ao buscar pedido');
  }
};

/**
 * Search for orders by user id
 */
export const getUserOrders = async (
  userId: string,
): Promise<OrderSummary[]> => {
  try {
    const ordersRef = collection(firestore, ORDERS_COLLECTION);
    const q = query(
      ordersRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
    );
    // console.log('🔍 Buscando pedidos do usuário:', userId);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        orderNumber: data.orderNumber,
        total: data.total,
        status: data.status,
        createdAt: data.createdAt?.toDate() || new Date(),
        itemCount: data.items?.length || 0,
        paymentStatus: data.payment?.status || 'pending',
      };
    });
  } catch (error) {
    console.error('Erro ao buscar pedidos do usuário:', error);
    throw new Error('Falha ao buscar pedidos');
  }
};

/**
 * Search for detailed orders by user id with full item information including beer data
 */
export const getUserOrdersDetailed = async (
  userId: string,
): Promise<OrderWithBeerData[]> => {
  try {
    const ordersRef = collection(firestore, ORDERS_COLLECTION);
    const q = query(
      ordersRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
    );
    // console.log('🔍 Buscando pedidos detalhados do usuário:', userId);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => {
      const order = firestoreToOrder(doc.id, doc.data());
      const enrichedItems = enrichOrderItemsWithBeerData(order.items);

      return {
        ...order,
        items: enrichedItems,
      };
    });
  } catch (error) {
    console.error('Erro ao buscar pedidos detalhados do usuário:', error);
    throw new Error('Falha ao buscar pedidos detalhados');
  }
};

/**
 * Atualiza o status de um pedido
 */
export const updateOrderStatus = async (
  orderId: string,
  status: Order['status'],
  updateData?: Partial<Order>,
): Promise<void> => {
  try {
    const docRef = doc(firestore, ORDERS_COLLECTION, orderId);
    const now = new Date();

    const updates: any = {
      status,
      updatedAt: Timestamp.fromDate(now),
      ...updateData,
    };

    // Adicionar timestamps específicos baseados no status
    switch (status) {
      case 'confirmed':
        updates.confirmedAt = Timestamp.fromDate(now);
        break;
      case 'shipped':
        updates.shippedAt = Timestamp.fromDate(now);
        break;
      case 'delivered':
        updates.deliveredAt = Timestamp.fromDate(now);
        break;
      case 'cancelled':
        updates.cancelledAt = Timestamp.fromDate(now);
        break;
    }

    await updateDoc(docRef, updates);
  } catch (error) {
    console.error('Erro ao atualizar status do pedido:', error);
    throw new Error('Falha ao atualizar pedido');
  }
};

/**
 * Atualiza informações de pagamento
 */
export const updateOrderPayment = async (
  orderId: string,
  paymentData: Partial<Order['payment']>,
): Promise<void> => {
  try {
    const docRef = doc(firestore, ORDERS_COLLECTION, orderId);
    const now = new Date();

    const updates: any = {
      payment: paymentData,
      updatedAt: Timestamp.fromDate(now),
    };

    // Adicionar timestamps específicos
    if (paymentData.status === 'approved' && !paymentData.approvedAt) {
      updates['payment.approvedAt'] = Timestamp.fromDate(now);
    }

    if (paymentData.status === 'refunded' && !paymentData.refundedAt) {
      updates['payment.refundedAt'] = Timestamp.fromDate(now);
    }

    await updateDoc(docRef, updates);
  } catch (error) {
    console.error('Erro ao atualizar pagamento do pedido:', error);
    throw new Error('Falha ao atualizar pagamento');
  }
};

/**
 * Atualiza informações de rastreamento
 */
export const updateOrderTracking = async (
  orderId: string,
  trackingData: Order['tracking'],
): Promise<void> => {
  try {
    const docRef = doc(firestore, ORDERS_COLLECTION, orderId);
    const now = new Date();

    await updateDoc(docRef, {
      tracking: {
        ...trackingData,
        lastUpdate: Timestamp.fromDate(trackingData?.lastUpdate || now),
      },
      updatedAt: Timestamp.fromDate(now),
    });
  } catch (error) {
    console.error('Erro ao atualizar rastreamento do pedido:', error);
    throw new Error('Falha ao atualizar rastreamento');
  }
};

/**
 * Busca pedidos com filtros (para admin)
 */
export const getOrdersWithFilters = async (
  filters: OrderFilters,
): Promise<Order[]> => {
  try {
    const ordersRef = collection(firestore, ORDERS_COLLECTION);
    let q = query(ordersRef, orderBy('createdAt', 'desc'));

    // Aplicar filtros
    if (filters.status && filters.status.length > 0) {
      q = query(q, where('status', 'in', filters.status));
    }

    if (filters.userId) {
      q = query(q, where('userId', '==', filters.userId));
    }

    if (filters.orderNumber) {
      q = query(q, where('orderNumber', '==', filters.orderNumber));
    }

    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => firestoreToOrder(doc.id, doc.data()));
  } catch (error) {
    console.error('Erro ao buscar pedidos com filtros:', error);
    throw new Error('Falha ao buscar pedidos');
  }
};

/**
 * Gera estatísticas de pedidos (para admin)
 */
export const getOrderStats = async (dateRange?: {
  start: Date;
  end: Date;
}): Promise<OrderStats> => {
  try {
    // Implementação básica - pode ser expandida conforme necessário
    const ordersRef = collection(firestore, ORDERS_COLLECTION);
    const snapshot = await getDocs(ordersRef);

    const orders = snapshot.docs.map(doc =>
      firestoreToOrder(doc.id, doc.data()),
    );

    const stats: OrderStats = {
      totalOrders: orders.length,
      totalRevenue: orders.reduce((sum, order) => sum + order.total, 0),
      averageOrderValue: 0,
      subscriberOrders: orders.filter(order => order.isSubscriber).length,
      regularOrders: orders.filter(order => !order.isSubscriber).length,
      statusBreakdown: {} as Record<Order['status'], number>,
      paymentStatusBreakdown: {} as Record<Order['payment']['status'], number>,
      topProducts: [],
    };

    stats.averageOrderValue = stats.totalRevenue / (stats.totalOrders || 1);

    return stats;
  } catch (error) {
    console.error('Erro ao gerar estatísticas:', error);
    throw new Error('Falha ao gerar estatísticas');
  }
};
